import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, Plus, Search, Pencil, Trash2, Phone, Mail } from "lucide-react";

const EMPTY = { nome: "", cognome: "", email: "", telefono: "", codice_fiscale: "", indirizzo: "", fonte: "Altro", note: "", stato: "attivo" };

export default function Pazienti() {
  const [clients, setClients] = useState([]);
  const [sources, setSources] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients", { params: q ? { search: q } : {} });
      setClients(data);
    } catch (e) { toast.error("Errore nel caricamento pazienti"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); api.get("/contact-sources").then(({ data }) => setSources(data)); }, [load]);
  useEffect(() => { const t = setTimeout(() => load(search), 300); return () => clearTimeout(t); }, [search, load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing) { await api.put(`/clients/${editing.id}`, payload); toast.success("Paziente aggiornato"); }
      else { await api.post("/clients", payload); toast.success("Paziente creato"); }
      setOpen(false); load(search);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/clients/${id}`); toast.success("Paziente eliminato"); load(search); }
    catch { toast.error("Errore eliminazione"); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  return (
    <div className="space-y-6">
      <PageHeader title="Anagrafica Clienti" subtitle={`${clients.length} pazienti registrati`}>
        <Button onClick={openNew} data-testid="btn-nuovo-paziente" className="font-semibold">
          <Plus className="mr-1.5 h-4 w-4" /> Nuovo Paziente
        </Button>
      </PageHeader>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per nome, telefono, email, CF..."
               data-testid="input-search-pazienti" className="pl-9 h-11 bg-white" />
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-8">Caricamento...</div>
      ) : clients.length === 0 ? (
        <EmptyState icon={Users} title="Nessun paziente" description="Aggiungi il tuo primo paziente per iniziare a gestire appuntamenti e trattamenti."
          action={<Button onClick={openNew}><Plus className="mr-1.5 h-4 w-4" /> Nuovo Paziente</Button>} />
      ) : (
        <Card className="overflow-hidden border-slate-200">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead>Paziente</TableHead>
                <TableHead className="hidden md:table-cell">Contatti</TableHead>
                <TableHead className="hidden lg:table-cell">Fonte</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.map((c) => (
                <TableRow key={c.id} data-testid={`client-row-${c.id}`} className="hover:bg-slate-50/60">
                  <TableCell>
                    <div className="font-medium text-slate-800">{c.cognome} {c.nome}</div>
                    {c.codice_fiscale && <div className="text-xs text-muted-foreground font-mono-data">{c.codice_fiscale}</div>}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <div className="space-y-0.5 text-sm">
                      {c.telefono && <div className="flex items-center gap-1.5 text-slate-600"><Phone className="h-3 w-3" />{c.telefono}</div>}
                      {c.email && <div className="flex items-center gap-1.5 text-slate-600"><Mail className="h-3 w-3" />{c.email}</div>}
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell"><Badge variant="secondary">{c.fonte}</Badge></TableCell>
                  <TableCell>
                    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${c.stato === "attivo" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {c.stato}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`delete-client-${c.id}`} className="text-destructive hover:text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Eliminare {c.nome} {c.cognome}?</AlertDialogTitle>
                            <AlertDialogDescription>L'operazione non può essere annullata.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annulla</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(c.id)} className="bg-destructive hover:bg-destructive/90">Elimina</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifica Paziente" : "Nuovo Paziente"}</DialogTitle>
            <DialogDescription>Compila i dati anagrafici del paziente.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Nome *</Label><Input required value={form.nome} onChange={set("nome")} data-testid="client-nome-input" className="mt-1.5" /></div>
              <div><Label>Cognome *</Label><Input required value={form.cognome} onChange={set("cognome")} data-testid="client-cognome-input" className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Telefono</Label><Input value={form.telefono} onChange={set("telefono")} data-testid="client-telefono-input" className="mt-1.5" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="client-email-input" className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Codice Fiscale</Label><Input value={form.codice_fiscale} onChange={set("codice_fiscale")} className="mt-1.5" /></div>
              <div>
                <Label>Fonte contatto</Label>
                <Select value={form.fonte} onValueChange={set("fonte")}>
                  <SelectTrigger className="mt-1.5" data-testid="client-fonte-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Indirizzo</Label><Input value={form.indirizzo} onChange={set("indirizzo")} className="mt-1.5" /></div>
            <div>
              <Label>Stato</Label>
              <Select value={form.stato} onValueChange={set("stato")}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="attivo">Attivo</SelectItem><SelectItem value="inattivo">Inattivo</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Note</Label><Textarea value={form.note} onChange={set("note")} className="mt-1.5" rows={3} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button type="submit" disabled={saving} data-testid="save-client-button">{saving ? "Salvataggio..." : "Salva"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
